import ContentImage from "@/components/ContentImage";
import { Link } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";
import GuideLearnMore from "@/components/guides/GuideLearnMore";
import { Button } from "@/components/ui/button";
import { Camera, Upload, Palette, DollarSign, Globe, Zap, Shield, BarChart3, ArrowRight, Check } from "lucide-react";
import heroImage from "@/assets/for-photographers-hero.jpg";
import photographerAction from "@/assets/photographer-action.jpg";

const ForPhotographers = () => {
  const features = [
    {
      icon: Upload,
      title: "Fast Bulk Uploads",
      description: "Our web uploader sends many photos at once and keeps adjusting to make full use of your bandwidth, so big folders finish sooner. If the connection drops, it resumes by itself.",
    },
    {
      icon: Zap,
      title: "AI-Powered Tagging",
      description: "Our AI automatically detects bib numbers, faces, and jersey colours in every photo. Participants find their shots instantly — no manual tagging required.",
    },
    {
      icon: Palette,
      title: "Custom Branded Galleries",
      description: "Create a professional storefront with your own logo, colour scheme, and custom domain. Your brand stays front and centre.",
    },
    {
      icon: DollarSign,
      title: "Flexible Pricing Controls",
      description: "Set individual photo prices, bundle deals, or offer free downloads — gallery by gallery. You decide how to monetise your work.",
    },
    {
      icon: Shield,
      title: "Watermark Protection",
      description: "Automatic watermarking protects your images until purchase. Your work stays secure while still being discoverable by participants.",
    },
    {
      icon: BarChart3,
      title: "Sales & Analytics Dashboard",
      description: "Track views, downloads, and revenue in real-time. Understand which events perform best and optimise your pricing strategy.",
    },
  ];

  const steps = [
    { number: "01", title: "Upload Your Photos", description: "Drop in a whole folder: light previews go up first, so your gallery is live in minutes." },
    { number: "02", title: "Brand Your Gallery", description: "Customise colours, logos, and layouts to match your brand identity." },
    { number: "03", title: "Choose Share or Sell", description: "Set each gallery to free downloads or paid sales — it's up to you." },
    { number: "04", title: "Go Live & Earn", description: "Publish your gallery and share the link. Connect a custom domain on Pro." },
  ];

  return (
    <div className="min-h-screen bg-background">
      <AppHeader variant="marketing" />

      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt="Event photographer capturing marathon runners" className="w-full h-full object-cover" width={1920} height={1080} />
          <div className="absolute inset-0 bg-gradient-to-r from-hawk-navy/95 via-hawk-navy/85 to-hawk-navy/60" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl text-white">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6">
              <Camera size={16} className="text-hawk-orange" />
              <span className="text-sm font-medium">For Photographers</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-6">
              Stay Behind the Lens, <span className="text-gradient-primary">Not the Laptop</span>
            </h1>
            <p className="text-lg md:text-xl text-white/80 mb-8 max-w-2xl">
              Upload your event photos, let AI handle the tagging, and start selling — all before your customers lose that finish-line feeling. Photohawk saves you hours so you can focus on what you do best.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/register">
                <Button variant="hero" size="lg" className="group">
                  Start Free Today
                  <ArrowRight className="group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/10">
                  View Pricing
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Problem / Solution */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-display font-bold mb-6">
                The Old Way of Selling Event Photos Is <span className="text-hawk-orange">Broken</span>
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  You spend hours shooting thousands of photos at a marathon, triathlon, or cycling race. Then you spend even more hours manually sorting, tagging by bib number, uploading to a clunky gallery, and hoping participants come back to buy.
                </p>
                <p>
                  By the time your gallery goes live, the excitement has faded. Participants have moved on, and your sales suffer. Sound familiar?
                </p>
                <p className="text-foreground font-medium">
                  Photohawk changes everything. Our AI processes your photos in minutes — not hours — so you can have your gallery live while participants are still buzzing from their race.
                </p>
              </div>
            </div>
            <div className="relative">
              {/* A real Photohawk Studio screenshot (src/content/media), listed in the image sitemap (src/lib/seo/pages.ts). */}
              <ContentImage name="web-uploader-live-on-previews" sizes="(min-width: 768px) 560px, 100vw" className="block h-auto w-full rounded-2xl shadow-xl" />
              <div className="absolute -bottom-4 -left-4 bg-hawk-orange text-white px-5 py-3 rounded-lg font-semibold shadow-lg">
                Photos online in minutes
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
              Everything You Need to <span className="text-hawk-orange">Grow Your Business</span>
            </h2>
            <p className="text-lg text-muted-foreground">
              From upload to sale, Photohawk automates the tedious work so you can shoot more events and earn more revenue.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {features.map((feature) => (
              <div key={feature.title} className="bg-card rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-hawk-orange to-hawk-gold flex items-center justify-center mb-5">
                  <feature.icon className="text-white" size={28} />
                </div>
                <h3 className="text-xl font-display font-bold mb-3">{feature.title}</h3>
                <p className="text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-4">
              From Shoot to Sale in <span className="text-hawk-orange">Four Steps</span>
            </h2>
          </div>
          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
            {steps.map((step) => (
              <div key={step.number} className="flex gap-5">
                <div className="text-5xl font-display font-bold text-hawk-orange/20">{step.number}</div>
                <div>
                  <h3 className="text-lg font-display font-bold mb-2">{step.title}</h3>
                  <p className="text-muted-foreground text-sm">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={photographerAction} alt="Photographer in action at an event" className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-hawk-navy/85" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-3xl md:text-4xl font-display font-bold mb-8">Trusted by Event Photographers Worldwide</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
              {[
                { value: "75M+", label: "Photos Uploaded" },
                { value: "450+", label: "Active Photographers" },
                { value: "29", label: "Countries" },
                { value: "10K+", label: "Events Powered" },
              ].map((stat) => (
                <div key={stat.label}>
                  <div className="text-3xl md:text-4xl font-display font-bold text-hawk-orange">{stat.value}</div>
                  <div className="text-sm text-white/60">{stat.label}</div>
                </div>
              ))}
            </div>
            <Link to="/register">
              <Button variant="hero" size="lg" className="group">
                Get Started Free
                <ArrowRight className="group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Photohawk */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-display font-bold text-center mb-12">
              Why Photographers Choose <span className="text-hawk-orange">Photohawk</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                "No upfront costs — pay only when you sell",
                "AI tags thousands of photos in minutes",
                "Custom branded galleries with your own domain",
                "Participants find their photos instantly",
                "Secure watermarking protects your work",
                "Real-time sales analytics and reporting",
                "A fast web uploader that resumes by itself, with FTP on demand if you need it",
                "Dedicated support from a team that understands photography",
              ].map((item) => (
                <div key={item} className="flex items-start gap-3 p-4 rounded-xl bg-secondary/50">
                  <Check className="text-hawk-orange mt-0.5 flex-shrink-0" size={20} />
                  <span className="text-sm font-medium">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <p className="container mx-auto max-w-4xl px-4 pt-4 text-center text-muted-foreground">
        Organising the event rather than shooting it?{" "}
        <Link to="/for-organisers" className="font-semibold text-hawk-orange hover:underline">
          Photohawk for organisers
        </Link>
      </p>

      <GuideLearnMore slugs={["sell-race-event-photos", "face-recognition-event-photos"]} />

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-hawk-orange to-hawk-gold">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
            Ready to Grow Your Event Photography Business?
          </h2>
          <p className="text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Join hundreds of photographers already using Photohawk to automate their workflow and increase sales.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-hawk-navy text-white hover:bg-hawk-navy/90 group text-lg px-8">
              Create Your Free Account
              <ArrowRight className="group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ForPhotographers;
